import lang from "core/constants/lang";
import format from "date-fns/format";
import { setSnackbar } from "store/commonActions";
import { dateFormat, SNACKBAR_TYPE } from "./viewUtility";

export const mimeTypeMap = {
	csv: "text/csv",
	pdf: "application/pdf",
	html: "text/html",
} as const;

export const apiEndpoints: { [key: string]: string } = {
	SIGN_IN: "/auth/v1",
	FETCH_TAB_AND_FILTERS: "/fetch-tabs-and-filters",
	FETCH_TRANSACTION_LIST: "/fetch-txns-list",
	FETCH_TRANSACTION_SUMMARY: "/fetch-txns-summary",
	UPDATE_PURCHASE_STATUS: "/update-dealer-approval",
	GET_DELIVERY_ORDER: "/get-delivery-order",
	DOWNLOAD_PURCHASE_DOCUMENT: "/download-purchase-document",
	UPLOAD_PURCHASE_DOCUMENT: "/upload-purchase-document",
};

export const updateObject = (
	oldObject: { [key: string | number]: any },
	updatedProperties: { [key: string | number]: any }
) => {
	return {
		...oldObject,
		...updatedProperties,
	};
};

export const checkValidity = (
	value: any,
	rules: any,
	controls: any,
	key?: any
): { isValid: boolean; invalidRule: string } => {
	let isValid = true;
	let invalidRule = "";

	if (!rules) {
		return { isValid: true, invalidRule: "" };
	}
	// TODO, currently its not handling optional values, We need to migrate all the check from requited if
	if (rules.required) {
		isValid =
			CommonUtil.isNotNull(value) && !CommonUtil.isEmptyObject(value) && String(value).trim() !== "" && isValid;
		invalidRule = !isValid && invalidRule === "" ? "required" : invalidRule;

		if (rules.minLength) {
			isValid = String(value).length >= rules.minLength && isValid;
			invalidRule = !isValid && invalidRule === "" ? "minLength" : invalidRule;
		}

		if (rules.isAlphabet) {
			isValid = CommonUtil.ALPHABET_REGEX.test(value) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isAlphabet" : invalidRule;
		}
		if (rules.isAlphabetSpace) {
			isValid = CommonUtil.ALPHABET_SPACE_REGEX.test(value) && isValid;
		}
		if (rules.isEmail) {
			const isEmailValid = CommonUtil.validateEmail(value);
			return { isValid: !!isEmailValid, invalidRule: "isEmail" };
		}

		if (rules.isMobile) {
			isValid = CommonUtil.validateMobile(value) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isMobile" : invalidRule;
		}
		if (rules.isNumeric) {
			const pattern = /^\d+$/;
			isValid = pattern.test(value) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isNumeric" : invalidRule;
			if (isValid) {
				if (rules.min) {
					isValid = Number(value) >= rules.min && isValid;
					invalidRule = !isValid && invalidRule === "" ? "min" : invalidRule;
				}
				if (rules.max) {
					isValid = Number(value) <= rules.max && isValid;
					invalidRule = !isValid && invalidRule === "" ? "max" : invalidRule;
				}
			}
		}

		if (rules.isPanCard) {
			const pattern = /^([330a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/;
			isValid = pattern.test(value) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isPanCard" : invalidRule;
		}
		if (rules.isPinCode) {
			const pattern = /^[1-9][0-9]{5}$/;
			isValid = pattern.test(value) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isPinCode" : invalidRule;
		}
		if (rules.isDate) {
			const pattern = /^(0[1-9]|1\d|2\d|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/;
			isValid = pattern.test(value) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isDate" : invalidRule;
		}
		if (rules.isDateTime && value === "Invalid Date" && isValid) {
			// This checks work for all the value coming from date-time picker component value === 'Invalid Date'
			// TODO: Fix this with actual date validation
			isValid = false;
			invalidRule = "isDateTime";
		}
		if (rules.isAfterNow) {
			const date = new Date(value);
			isValid = date.getTime() > Date.now() && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isAfterNow" : invalidRule;
		}
		if (rules.isAfterStart) {
			isValid = rules.isAfterStart(controls) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isAfterStart" : invalidRule;
		}
		if (CommonUtil.isNotNull(rules.isMinValue)) {
			isValid = value >= parseInt(rules.isMinValue) && isValid;
			invalidRule = !isValid && invalidRule === "" ? "isMinValue" : invalidRule;
		}
		if (rules.fileType) {
			isValid = value?.type === mimeTypeMap[rules.fileType as keyof typeof mimeTypeMap] && isValid;
			invalidRule = !isValid && invalidRule === "" ? "fileType" : invalidRule;
		}

		if (rules.duplicate) {
			const currentControl = controls[key];
			isValid = currentControl.valid.touched ? isValid : currentControl.valid.isValid && isValid;
			invalidRule = !isValid && invalidRule === "" ? "duplicate" : invalidRule;
		}
	}

	if (rules.maxLength && typeof value.length !== "undefined") {
		isValid = value.length <= rules.maxLength && isValid;
		invalidRule = !isValid && invalidRule === "" ? "maxLength" : invalidRule;
	}

	if (rules.custom) {
		isValid = rules.custom(controls);
		invalidRule = !isValid && invalidRule === "" ? "custom" : invalidRule;
	}

	return {
		isValid,
		invalidRule,
	};
};

export const CommonUtil = {
	RES_PATH: "https://dugtmg0pklp2w.cloudfront.net/",
	EMAIL_REGEX:
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
	ALPHABET_REGEX: /^[a-zA-Z]*$/,
	NAME: /^[a-z .]+$/i,
	ALPHABET_SPACE_REGEX: /^[a-zA-Z ]*$/,
	MOBILE_REGEX: /^[6789]\d{9}$/,
	DIGIT_REGEX: /^[0-9\b]+$/,
	// Masking email Regex
	BASIC_EMAIL_MASK: /^([@])/g,
	INR: "₹",
	trimText(str: string, lenght = 30) {
		if (str?.substring) {
			if (str.length > lenght) {
				return str.substring(0, lenght).trim() + "...";
			} else {
				return str;
			}
		} else {
			return str;
		}
	},
	validURL(str: string) {
		const pattern = new RegExp(
			"^(https?:\\/\\/)?" +
				"((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
				"((\\d{1,3}\\.){3}\\d{1,3}))" +
				"(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
				"(\\?[;&a-z\\d%_.~+=-]*)?" +
				"(\\#[-a-z\\d_]*)?$",
			"i"
		);
		return !!pattern.test(str);
	},
	isLoggedIn() {
		return false;
	},
	isError(controls: any) {
		return controls.valid && !controls.valid.isValid && controls.touched;
	},
	getHelperTxt(controls: any) {
		// if required,touched and value is entered and then not valid : inValid Error
		// else no value being entered

		if (controls.validations && controls.touched && !controls.valid.isValid) {
			return controls.validations.messages[controls.valid.invalidRule];
		}

		if (controls.dynamicHelperTextHeight) {
			return controls.helperText ? controls.helperText : "";
		}

		if (controls.validations) {
			// Empty sting is  added to have pre-allocated space for error messages
			// to prevent flickering.  We only added empty string  if validation rules prevent
			return controls.helperText ? controls.helperText : " ";
		}
		return controls.helperText;
	},
	isNull(data: any) {
		return data === undefined || data === null;
	},
	isNotNull(data: any) {
		return !this.isNull(data);
	},
	isObject(data: any) {
		// This will handling all primitive data type and array,
		// other non primitive data type and constructor value will be passed ex: String(), etc
		return data !== null && typeof data === "object" && !Array.isArray(data);
	},
	isEmptyFormValue(data: any) {
		return data === undefined || data === null || data === "";
	},
	isEmptyObject(data: any) {
		if (this.isNotNull(data) && typeof data === "object" && Object.getPrototypeOf(data) === Object.prototype) {
			return Object.keys(data).length === 0;
		}
		return false;
	},
	validateEmail(value: string) {
		const email = String(value).toLowerCase();
		return email && this.EMAIL_REGEX.test(email);
	},

	validateMobile(number: string): boolean {
		return !!number && this.MOBILE_REGEX.test(String(number));
	},
	toMoney(amount: string) {
		return Math.ceil(parseFloat(amount)).toLocaleString("en-IN");
	},
	toPercent(value: string) {
		if (value === "" || value === undefined || value === null) {
			return value;
		}
		value = String(Math.round((Number(value) + Number.EPSILON) * 100) / 100);

		return `${value}%`;
	},
	toMoneyWithSymbol(amount: any, delimiter?: string) {
		if (typeof delimiter === "undefined") {
			delimiter = "";
		}

		if (amount === "") {
			return "";
		}

		if (CommonUtil.isNotNull(amount)) {
			const amt = amount > 0 ? amount : 0;
			return this.INR + delimiter + this.toMoney(amt);
		}
	},
	handleNull(value: string, options: { placeHolder?: string; suffix?: string }) {
		options = { suffix: "", placeHolder: "-", ...options };
		if (typeof value === "undefined" || value === null || value === "") {
			return options.placeHolder;
		}
		return options.suffix ? value + options.suffix : value;
	},
	replace(str: string, mapList: any) {
		mapList.forEach((element: any) => {
			const replace = element.placeholder;
			const re = new RegExp(replace, "g");
			str = str.replace(re, element.value);
		});
		return str;
	},
	downloadURI(uri: string, name: string) {
		const link = document.createElement("a");
		link.download = name;
		link.href = uri;
		link.style.display = "none";
		link.click();
		link.remove();
	},
	toMonthText(value: string) {
		if (value) return value + " Mo.";
	},
	openInNewTab(url: string) {
		url && window.open(url, "_blank");
	},
	formatDateStringToServerDate(dateString: string) {
		return format(new Date(dateString), dateFormat.formatDateTimeServer);
	},
	randomItemFromArray(items: any[]) {
		return items[Math.floor(Math.random() * items.length)];
	},
	kFormatter(num: number) {
		return Math.abs(num) > 999
			? (Math.sign(num) * Number((Math.abs(num) / 1000).toFixed(1))).toString() + "k"
			: Math.sign(num) * Math.abs(num);
	},
	camelCaseToDisplayString(str: string) {
		return (
			str
				.replace(/([a-z])([A-Z])/g, "$1 $2")
				// Capitalize the first letter of the string
				.replace(/^./, function (match) {
					return match.toUpperCase();
				})
		);
	},
	formatDate(date?: Date) {
		return date
			?.toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			})
			.replace(/\//g, "-");
	},
};

export const preventOtherEvents = (e: any) => {
	e.preventDefault();
	e.stopPropagation();
};

export const handleCopyClick = (content: string, dispatch: any) => {
	navigator.clipboard.writeText(content);
	dispatch(
		setSnackbar({
			type: SNACKBAR_TYPE.SUCCESS,
			message: lang.CONTENT_COPIED,
		})
	);
};

export const deleteDynamicKey = () => {};
export default CommonUtil;
